import { z } from "zod";

export const DEFAULT_AGREEMENT_LINK_DAYS = 30;
export const agreementSignerRoleSchema = z.enum(["recipient", "company"]);
export type AgreementSignerRole = z.infer<typeof agreementSignerRoleSchema>;
export const agreementRequestStateSchema = z.enum([
  "draft",
  "awaiting_recipient",
  "awaiting_company",
  "finalizing",
  "completed",
  "declined",
  "expired",
  "voided",
]);
export type AgreementRequestState = z.infer<typeof agreementRequestStateSchema>;

/** Plain-language status for dashboard lists. The raw state names describe
 * the internal lifecycle and read as a blocker to admins ("awaiting company"). */
export const AGREEMENT_STATE_LABELS: Record<AgreementRequestState, string> = {
  draft: "Draft",
  awaiting_recipient: "Sent - awaiting signature",
  awaiting_company: "Signed - awaiting admin acknowledgment",
  finalizing: "Signed - preparing final PDF",
  completed: "Completed",
  declined: "Declined",
  expired: "Expired",
  voided: "Voided",
};

export function agreementStateLabel(state: string): string {
  return (
    AGREEMENT_STATE_LABELS[state as AgreementRequestState] ??
    state.replaceAll("_", " ")
  );
}

const label = z.string().trim().min(1).max(200);
export const agreementSha256Schema = z.string().regex(/^[a-f0-9]{64}$/);

// Coordinates are fractions of the rotated crop-box viewport, with a top-left
// origin. The PDF adapter must transform these to PDF coordinates explicitly.
export const agreementFieldBoxSchema = z
  .strictObject({
    pageIndex: z.number().int().min(0).max(499),
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    width: z.number().positive().max(1),
    height: z.number().positive().max(1),
  })
  .refine(box => box.x + box.width <= 1 && box.y + box.height <= 1, {
    message: "The field must fit inside its page.",
  });

const baseField = {
  id: z.string().regex(/^[a-zA-Z][a-zA-Z0-9_]{0,63}$/),
  label,
  role: agreementSignerRoleSchema,
  required: z.boolean(),
  box: agreementFieldBoxSchema,
};

export const agreementFieldSchema = z.discriminatedUnion("kind", [
  z.strictObject({ ...baseField, kind: z.literal("signature") }),
  z.strictObject({ ...baseField, kind: z.literal("printed_name") }),
  z.strictObject({ ...baseField, kind: z.literal("title") }),
  z.strictObject({ ...baseField, kind: z.literal("signed_date") }),
  z.strictObject({
    ...baseField,
    kind: z.literal("text"),
    maxLength: z.number().int().min(1).max(4000),
  }),
  z.strictObject({ ...baseField, kind: z.literal("checkbox") }),
  z
    .strictObject({
      ...baseField,
      kind: z.literal("selection"),
      options: z
        .array(label)
        .min(1)
        .max(100)
        .refine(
          options => new Set(options).size === options.length,
          "Selection options must be unique."
        ),
      optionBoxes: z.array(agreementFieldBoxSchema).min(1).max(100).optional(),
    })
    .refine(
      field =>
        field.optionBoxes === undefined ||
        field.optionBoxes.length === field.options.length,
      { message: "Selection option boxes must match the available options." }
    ),
]);

export type AgreementField = z.infer<typeof agreementFieldSchema>;

export const agreementAttachmentContentTypeSchema = z.enum([
  "application/pdf",
  "image/png",
  "image/jpeg",
]);

export const agreementAttachmentRequirementSchema = z.strictObject({
  id: z.string().regex(/^[a-z][a-z0-9_]{0,19}$/),
  label,
  description: z.string().trim().min(1).max(500),
  required: z.boolean(),
  allowedContentTypes: z
    .array(agreementAttachmentContentTypeSchema)
    .min(1)
    .max(3)
    .refine(values => new Set(values).size === values.length, {
      message: "Attachment content types must be unique.",
    }),
  maxBytes: z
    .number()
    .int()
    .min(1024)
    .max(25 * 1024 * 1024),
});

export type AgreementAttachmentRequirement = z.infer<
  typeof agreementAttachmentRequirementSchema
>;

export function agreementAttachmentStage(requirementId: string): string {
  return `attachment_${agreementAttachmentRequirementSchema.shape.id.parse(requirementId)}`;
}

// This validates a complete configuration, not an editable draft. Activation
// also requires approved rendering and current signer authority in the service.
export const agreementTemplateConfigurationSchema = z
  .strictObject({
    title: label,
    contractingEntity: label,
    sourceSha256: agreementSha256Schema,
    pageCount: z.number().int().min(1).max(500),
    requiresCompanySignature: z.boolean(),
    disclosureVersion: z.string().trim().min(1).max(100),
    disclosureText: z.string().trim().min(1).max(10000),
    linkValidityDays: z
      .number()
      .int()
      .min(1)
      .max(365)
      .default(DEFAULT_AGREEMENT_LINK_DAYS),
    attachments: z
      .array(agreementAttachmentRequirementSchema)
      .max(10)
      .default([]),
    fields: z.array(agreementFieldSchema).min(3).max(500),
  })
  .superRefine((template, ctx) => {
    const ids = new Set<string>();
    const attachmentIds = new Set<string>();
    template.attachments.forEach((attachment, index) => {
      if (attachmentIds.has(attachment.id)) {
        ctx.addIssue({
          code: "custom",
          path: ["attachments", index, "id"],
          message: "Attachment IDs must be unique.",
        });
      }
      attachmentIds.add(attachment.id);
    });
    template.fields.forEach((field, index) => {
      if (ids.has(field.id)) {
        ctx.addIssue({
          code: "custom",
          path: ["fields", index, "id"],
          message: "Field IDs must be unique.",
        });
      }
      ids.add(field.id);
      if (field.box.pageIndex >= template.pageCount) {
        ctx.addIssue({
          code: "custom",
          path: ["fields", index, "box"],
          message: "The field references a missing page.",
        });
      }
      if (
        field.kind === "selection" &&
        field.optionBoxes?.some(box => box.pageIndex >= template.pageCount)
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["fields", index, "optionBoxes"],
          message: "A selection option references a missing page.",
        });
      }
      if (field.role === "company" && !template.requiresCompanySignature) {
        ctx.addIssue({
          code: "custom",
          path: ["fields", index, "role"],
          message: "Company fields require company signing.",
        });
      }
    });
    const roles: AgreementSignerRole[] = template.requiresCompanySignature
      ? ["recipient", "company"]
      : ["recipient"];
    for (const role of roles) {
      const kinds =
        role === "company"
          ? ["signature", "printed_name", "title"]
          : ["signature", "printed_name", "signed_date"];
      for (const kind of kinds) {
        if (
          !template.fields.some(
            field =>
              field.role === role && field.kind === kind && field.required
          )
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["fields"],
            message: `A required ${role} ${kind} field is missing.`,
          });
        }
      }
    }
  });

export type AgreementTemplateConfiguration = z.infer<
  typeof agreementTemplateConfigurationSchema
>;

// IDs must be resolved and authorized server-side before this snapshot is made.
// No foreign-key cascade to the contact projection should delete this identity.
export const agreementRecordReferenceSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("lead"), id: z.number().int().positive() }),
  z.strictObject({ kind: z.literal("rep"), id: z.number().int().positive() }),
]);

export const agreementRecipientSnapshotSchema = z.strictObject({
  record: agreementRecordReferenceSchema,
  printedName: label,
  email: z.email().max(320),
  vertical: z.string().trim().min(1).max(100),
  externalContactId: z.string().trim().min(1).max(100).nullable(),
  externalOpportunityId: z.string().trim().min(1).max(100).nullable(),
});

const fieldValuesSchema = z.record(
  z.string(),
  z.union([z.string().max(4000), z.boolean()])
);

/** Validate only signer-entered fields. Signature, identity and acceptance time
 * are separate signing evidence and must never be accepted through this map. */
export function parseAgreementFieldValues(
  configuration: AgreementTemplateConfiguration,
  role: AgreementSignerRole,
  input: unknown
): Record<string, string | boolean> {
  const template = agreementTemplateConfigurationSchema.parse(configuration);
  const signerRole = agreementSignerRoleSchema.parse(role);
  if (signerRole === "company" && !template.requiresCompanySignature) {
    throw new Error("This agreement has no company signing step.");
  }
  const values = fieldValuesSchema.parse(input);
  const editable = template.fields.filter(
    field =>
      field.role === signerRole &&
      ["text", "checkbox", "selection"].includes(field.kind)
  );
  const editableIds = new Set(editable.map(field => field.id));
  for (const id of Object.keys(values)) {
    if (!editableIds.has(id))
      throw new Error("An unassigned field was submitted.");
  }
  const result: Record<string, string | boolean> = Object.create(null);
  for (const field of editable) {
    const value = Object.hasOwn(values, field.id)
      ? values[field.id]
      : undefined;
    if (value === undefined) {
      if (field.required) throw new Error(`Complete ${field.label}.`);
      continue;
    }
    if (field.kind === "checkbox") {
      if (typeof value !== "boolean" || (field.required && !value)) {
        throw new Error(`Confirm ${field.label}.`);
      }
      result[field.id] = value;
      continue;
    }
    if (typeof value !== "string") throw new Error(`Check ${field.label}.`);
    const text = value.trim();
    if (field.required && !text) throw new Error(`Complete ${field.label}.`);
    if (!text && !field.required) continue;
    if (field.kind === "text" && text.length > field.maxLength) {
      throw new Error(`The value for ${field.label} is too long.`);
    }
    if (field.kind === "selection" && !field.options.includes(text)) {
      throw new Error(`Choose an available option for ${field.label}.`);
    }
    result[field.id] = text;
  }
  return result;
}
